'use client';

import { useCallback } from 'react';
import {
  toasterApi,
  type ExternalNetworkSettingsPersistRequest,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useUpdateExternalNetworkSurface } from '../../../../hooks/api/useSurfaceSettings';

export function usePersistExternalNetworkSettings({
  surfaceId,
  isReadOnly,
}: {
  surfaceId: string;
  isReadOnly: boolean;
}) {
  const t = useTranslations('externalNetwork.settings');
  const { mutateAsync, isPending } = useUpdateExternalNetworkSurface(surfaceId);

  const persist = useCallback(
    async ({
      updates,
      showSuccessToast,
    }: ExternalNetworkSettingsPersistRequest): Promise<boolean> => {
      if (isReadOnly) {
        return false;
      }

      try {
        await mutateAsync(updates);

        if (showSuccessToast) {
          toasterApi.success({ title: t('saveSuccess') });
        }

        return true;
      } catch (error: unknown) {
        console.error(
          new Error('Failed to update external network settings', {
            cause: error instanceof Error ? error : new Error(String(error)),
          }),
        );
        toasterApi.error({ title: t('saveError') });

        return false;
      }
    },
    [isReadOnly, mutateAsync, t],
  );

  return { isUpdating: isPending, persist };
}
